import { LoadingSpinner } from '../shared/components';
import { Formatters } from '../shared/lib/formatters';
import { icon } from '../shared/components/icons';
import { emptyState } from '../shared/components/Narrative';
import { openExecDrill } from '../shared/components/ExecDrill';
// R115 fix (review exec#2): a card whose source failed renders amber with the
// error — never a green "all clear" (lib/execCards, tested).
import { cardState } from '../../../lib/execCards.mjs';

// R87 #4 (Jesse: "Let's create a tab that's called Executive Summary"): the
// portfolio's morning read for the leadership seat — fourteen KPIs from
// /api/exec-summary, each carrying its own basis line and failing alone
// (a broken source nulls its card, never the page). Admin + director only
// (the hub tab is finGated to match the server gate).
// R115 C6 (Jesse: "cards clickable → drill-down popup"): every card with a
// number opens the shared ExecDrill sheet (body-mounted — this page is
// re-mounted on every hub render) listing the rows behind that number, from
// GET /api/exec/drill/:card (the same selector the card's count uses).
const esc = (x: any) => Formatters.escapeHtml(String(x == null ? '' : x));

export class ExecSummaryPage {
  private container: HTMLElement;
  private data: any = null;

  constructor(container: HTMLElement) { this.container = container; }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="xs-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    try {
      const r = await fetch('/api/exec-summary');
      this.data = r.ok ? await r.json() : { error: true, status: r.status };
    } catch { this.data = { error: true }; }
    this.draw();
  }

  // key → the card's drill (role=button, keyboard-reachable). A card whose
  // value is null (its source failed) stays static — nothing to list.
  private card(label: string, ico: string, value: string, cap: string, basis: string, tone = '', key = '', clickable = true): string {
    const on = !!(key && clickable);
    const attrs = on
      ? ` data-xs-card="${esc(key)}" data-xs-label="${esc(label)}" role="button" tabindex="0" aria-haspopup="dialog" aria-label="${esc(label)} — open the list"`
      : '';
    return `
      <div class="kpi-card ${tone}${on ? ' clickable' : ''}"${attrs} title="${esc(basis)}">
        <div class="kpi-card-top"><span class="kpi-card-label">${esc(label)}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value num">${value}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;
  }

  private trend(delta: number | null | undefined, suffix = '', goodWhenUp: boolean | null = true): string {
    if (delta == null || !Number.isFinite(Number(delta))) return '';
    const up = Number(delta) > 0;
    const flat = Number(delta) === 0;
    const good = flat || goodWhenUp == null ? null : (up === goodWhenUp);
    const color = good == null ? 'var(--text-faint)' : good ? 'var(--success-text,#065f46)' : 'var(--danger-text,#991b1b)';
    return ` <span style="color:${color}; font-weight:600;">${flat ? '→' : up ? '▲' : '▼'} ${flat ? 'flat' : `${Math.abs(Number(delta))}${suffix}`}</span>`;
  }

  private draw(): void {
    const body = document.getElementById('xs-body');
    if (!body) return;
    const j = this.data;
    if (!j || j.error) {
      body.innerHTML = j && j.status === 403
        ? emptyState('Executive Summary', 'Limited to Admin and Director roles.', '🔒')
        : emptyState('Executive Summary unavailable', 'GET /api/exec-summary failed — check the server log.', '⚠️');
      return;
    }
    const k = j.kpis || {};
    const g = (key: string) => k[key] || { value: null, basis: '' };
    const na = '<span style="color:var(--text-faint); font-size:20px;">—</span>';
    const num = (v: any) => (v == null ? na : esc(String(v)));

    const rs = g('review_score');
    const bk = g('bookings_24h');
    const mtr = g('mtr_open');
    const win = g('adr_occ_revpar');
    const w = win.value || {};
    const dp = win.diff_pct || {};
    const bl = g('blocks_no_note');
    const pm = g('profit_margin');
    const nps = g('owner_nps');
    const yt = g('properties_added_ytd');
    const su = g('listings_suspended');
    const td = g('tasks_past_due');
    const ar = g('at_risk_arrivals');
    const pf = g('payment_flags');
    const iu = g('inspections_unassigned_7d');
    const cu = g('cleans_unassigned_7d');
    // R115 fix: every card's failed / tone / clickable state from ONE rule
    // (lib/execCards): a null value with an error is amber + the error, never
    // the green "all clear" or an "empty" caption.
    const S = {
      rs: cardState(rs, 'neutral', { emptyOk: true }),
      bk: cardState(bk, 'neutral'),
      mtr: cardState(mtr, 'neutral'),
      pm: cardState(pm, 'neutral', { emptyOk: true }),
      win: cardState(win, 'neutral'),
      nps: cardState(nps, 'neutral', { emptyOk: true }),
      yt: cardState(yt, 'growth'),
      su: cardState(su, 'exception'),
      bl: cardState(bl, 'exception'),
      td: cardState(td, 'exception'),
      ar: cardState(ar, 'exception'),
      pf: cardState(pf, 'exception'),
      iu: cardState(iu, 'exception'),
      cu: cardState(cu, 'exception'),
    };
    // hover = how it's computed (+ why it's blank when the source failed)
    const tip = (x: any): string => `${x.basis || ''}${x.value == null && x.error ? ` — ${x.error}` : ''}`;

    const windowRow = (label: string, ww: any, diffs: any) => ww ? `
      <div style="display:flex; gap:14px; align-items:baseline; flex-wrap:wrap; padding:4px 0;">
        <span style="font-size:11px; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); min-width:92px;">${esc(label)}</span>
        <span class="num" style="font-size:15px;"><strong>${ww.occ == null ? '—' : `${esc(ww.occ)}%`}</strong> <span style="font-size:10.5px; color:var(--text-muted);">OCC</span>${diffs ? this.trend(diffs.occ, '%', null) : ''}</span>
        <span class="num" style="font-size:15px;"><strong>${ww.adr == null ? '—' : `$${esc(ww.adr)}`}</strong> <span style="font-size:10.5px; color:var(--text-muted);">ADR</span>${diffs ? this.trend(diffs.adr, '%', null) : ''}</span>
        <span class="num" style="font-size:15px;"><strong>${ww.revpar == null ? '—' : `$${esc(ww.revpar)}`}</strong> <span style="font-size:10.5px; color:var(--text-muted);">RevPAR</span>${diffs ? this.trend(diffs.revpar, '%', null) : ''}</span>
      </div>` : '';

    body.innerHTML = `
      <div class="dash-head">
        <h1>Executive Summary</h1>
        <p>The portfolio at a glance — reviews, pace, money, and the operational exceptions that need an owner today. Click any card for the list behind its number; hover for exactly how it's computed. As of ${esc(j.as_of)}.</p>
      </div>
      <style>
        #xs-body [data-xs-card] { cursor:pointer; }
        #xs-body [data-xs-card]:focus-visible { outline:none; box-shadow:var(--focus-ring); }
        #xs-body .xs-pace[data-xs-card] { transition:box-shadow .15s ease, border-color .15s ease; }
        #xs-body .xs-pace[data-xs-card]:hover { box-shadow:var(--shadow-hover); border-color:var(--border-strong); }
      </style>
      <div class="kpi-section-label">Guest & revenue pulse</div>
      <div class="kpi-grid">
        ${this.card('Review score · 30d', 'star', rs.value == null ? na : `${esc(rs.value)}<span style="font-size:14px; color:var(--text-muted);">/5</span>`, S.rs.failed ? esc(S.rs.cap) : rs.value == null ? 'no reviews in the window' : `${rs.n || 0} review${rs.n === 1 ? '' : 's'}${this.trend(rs.delta, '')} vs prior 30d`, tip(rs), S.rs.failed ? S.rs.tone : rs.value != null && rs.value >= 4.7 ? 'tone-good' : '', 'review_score', S.rs.clickable)}
        ${this.card('Bookings · 24h', 'rocket', num(bk.value), S.bk.failed ? esc(S.bk.cap) : `yesterday ${esc(bk.prev ?? '—')}${this.trend(bk.delta, '', null)}`, tip(bk), S.bk.tone, 'bookings_24h', S.bk.clickable)}
        ${this.card('Open MTRs', 'building', num(mtr.value), S.mtr.failed ? esc(S.mtr.cap) : `of ${esc(mtr.of)} in the MTR set — vacant now${mtr.checking_out_today ? ` · +${esc(mtr.checking_out_today)} checking out today` : ''}`, tip(mtr), S.mtr.failed ? S.mtr.tone : mtr.value ? 'tone-bad' : '', 'mtr_open', S.mtr.clickable)}
        ${this.card('Profit margin', 'wallet', pm.value == null ? na : `${esc(pm.value)}%`, S.pm.failed ? esc(S.pm.cap) : pm.value == null ? 'no expenses recorded for the month yet' : `${esc(pm.month)}${this.trend(pm.delta, 'pt')} vs ${esc(pm.prev_month)}`, tip(pm), S.pm.tone, 'profit_margin', !S.pm.failed)}
      </div>
      <div class="card card-elev xs-pace" style="margin:16px 0; padding:12px 18px;" title="${esc(tip(win))}"${win.value ? ' data-xs-card="adr_occ_revpar" data-xs-label="Pace — trailing vs next 30 days" role="button" tabindex="0" aria-haspopup="dialog" aria-label="Pace — open the per-listing list"' : ''}>
        <div style="font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin-bottom:2px;">Pace — trailing vs next 30 days</div>
        ${win.value ? `${windowRow('Last 30 days', w.last30, null)}${windowRow('Next 30 days', w.next30, dp)}` : `<div style="font-size:12.5px; color:var(--warning-text, #92400e);">${esc(S.win.cap || 'unavailable right now (not zero)')}</div>`}
      </div>
      <div class="kpi-section-label">Owners & growth</div>
      <div class="kpi-grid">
        ${this.card('Owner NPS', 'heart', nps.value == null ? na : `${nps.value > 0 ? '+' : ''}${esc(nps.value)}`, S.nps.failed ? esc(S.nps.cap) : nps.value == null ? 'no survey responses yet — send links from owner profiles' : `${esc(nps.n)} response${nps.n === 1 ? '' : 's'} · trailing 365d`, tip(nps), S.nps.failed ? S.nps.tone : nps.value != null ? (nps.value >= 30 ? 'tone-good' : nps.value < 0 ? 'tone-bad' : '') : '', 'owner_nps', !S.nps.failed)}
        ${this.card('Properties added YTD', 'sparkles', num(yt.value), S.yt.failed ? esc(S.yt.cap) : 'first booking landed this year', tip(yt), S.yt.tone, 'properties_added_ytd', S.yt.clickable)}
        ${this.card('Suspended listings', 'building', num(su.value), S.su.failed
          ? esc(S.su.cap)
          : su.value
            ? esc((su.names || []).join(' · ').slice(0, 90))
            : `none suspended · off-channel: ${esc(su.gaps?.airbnb ?? '—')} Airbnb / ${esc(su.gaps?.vrbo ?? '—')} Vrbo / ${esc(su.gaps?.bookingcom ?? '—')} Booking`, tip(su), S.su.tone, 'listings_suspended', S.su.clickable)}
        ${this.card('Blocks without notes', 'calendar', num(bl.value), S.bl.cap ? esc(S.bl.cap) : 'blocked ranges nobody has explained', tip(bl), S.bl.tone, 'blocks_no_note', S.bl.clickable)}
        ${this.card('Past-due tasks', 'flag', num(td.value), S.td.cap ? esc(S.td.cap) : 'open tasks past their due date', tip(td), S.td.tone, 'tasks_past_due', S.td.clickable)}
      </div>
      <div class="kpi-section-label">Operational exceptions — next 7 days</div>
      <div class="kpi-grid">
        ${this.card('At-risk arrivals', 'rocket', num(ar.value), S.ar.cap ? esc(S.ar.cap) : 'inside 48h, checklist not verified', tip(ar), S.ar.tone, 'at_risk_arrivals', S.ar.clickable)}
        ${this.card('Payment flags', 'wallet', num(pf.value), S.pf.cap ? esc(S.pf.cap) : 'unpaid or second payment due', tip(pf), S.pf.tone, 'payment_flags', S.pf.clickable)}
        ${this.card('Inspections unassigned', 'search', num(iu.value), S.iu.cap ? esc(S.iu.cap) : 'checkouts next 7d with no inspector', tip(iu), S.iu.tone, 'inspections_unassigned_7d', S.iu.clickable)}
        ${this.card('Cleans unassigned', 'flag', num(cu.value), S.cu.cap ? esc(S.cu.cap) : 'turns next 7d with no cleaner', tip(cu), S.cu.tone, 'cleans_unassigned_7d', S.cu.clickable)}
      </div>`;
    this.bindDrills(body);
  }

  // One delegated click + Enter/Space handler per #xs-body (it survives
  // draw(); a fresh render() makes a fresh element).
  private bindDrills(body: HTMLElement): void {
    if ((body as any).__xsBound) return;
    (body as any).__xsBound = true;
    const open = (el: HTMLElement) => {
      const key = el.dataset.xsCard || '';
      if (!key) return;
      const v = el.querySelector('.kpi-card-value');
      openExecDrill(key, {
        label: el.dataset.xsLabel || '',
        value: v ? (v.textContent || '').trim() : '',
        basis: el.getAttribute('title') || '',
        hostPage: 'analytics',
        returnFocus: el,
      });
    };
    body.addEventListener('click', (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-xs-card]');
      if (el && body.contains(el)) open(el);
    });
    body.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-xs-card]');
      if (!el || el !== e.target) return;
      e.preventDefault();
      open(el);
    });
  }
}
